import { rufe } from './client';

/** WEB-02: die Anfragen aus den Formularen des Portals (Migration 0257). */
export type Leadart = 'allgemein' | 'rueckruf' | 'firmenanfrage' | 'bewerbung';
export type Leadstatus = 'neu' | 'zugeordnet' | 'erledigt' | 'verworfen';

export const LEADARTNAME: Readonly<Record<Leadart, string>> = {
  allgemein: 'Kontakt',
  rueckruf: 'Rückruf',
  firmenanfrage: 'Firmenanfrage',
  bewerbung: 'Bewerbung',
};

export const LEADSTATUSNAME: Readonly<Record<Leadstatus, string>> = {
  neu: 'offen',
  zugeordnet: 'zugeordnet',
  erledigt: 'erledigt',
  verworfen: 'verworfen',
};

/** KUN-23: woran ein Verdacht hängt. */
export const VERDACHTSGRUND: Readonly<Record<string, string>> = {
  email: 'gleiche E-Mail',
  telefon: 'gleiche Telefonnummer',
  name_plz: 'ähnlicher Name, gleiche PLZ',
  name: 'ähnlicher Name',
};

export type Leadzeile = {
  readonly id: string;
  readonly vorgangsnummer: string;
  readonly art: Leadart;
  readonly name: string;
  readonly email: string;
  readonly telefon: string | null;
  readonly betreff: string | null;
  readonly status: Leadstatus;
  readonly portal: string;
  readonly quelle: string;
  readonly eingangAm: string;
  readonly verdachtsfaelle: number;
  readonly kunde: string | null;
  readonly kundennummer: string | null;
};

export type Leadverdacht = {
  readonly kunde: string;
  readonly kundennummer: string;
  readonly name: string;
  readonly ort: string | null;
  readonly guete: number;
  readonly grund: string;
};

/** LEG-12: worin, in welcher Fassung und wann eingewilligt wurde. */
export type Einwilligungsnachweis = {
  readonly fassung: string | null;
  readonly wortlaut: string | null;
  readonly erteiltAm: string | null;
};

export type Leadakte = Leadzeile & {
  readonly nachricht: string | null;
  readonly erreichbarkeit: string | null;
  readonly werbeeinwilligung: boolean;
  readonly einwilligung: Einwilligungsnachweis | null;
  /** WEB-02: einem vorhandenen Kunden zugeordnet oder als neuer Kunde angelegt. */
  readonly uebernahme: 'bestand' | 'neuanlage' | null;
  readonly angaben: Readonly<Record<string, unknown>>;
  readonly dublettenverdacht: readonly Leadverdacht[];
  readonly bearbeitetAm: string | null;
  readonly bearbeitetVon: string | null;
  readonly bearbeitungsgrund: string | null;
};

export async function liste(status: Leadstatus | null): Promise<readonly Leadzeile[]> {
  const { leads } = await rufe<{ leads: readonly Leadzeile[] }>(
    status === null ? '/leads' : `/leads?status=${status}`,
  );
  return leads;
}

export async function akte(id: string): Promise<Leadakte> {
  return rufe(`/leads/${id}`);
}

/** KUN-15: die Wiedervorlage, die mit der Übernahme entsteht. */
export type Leadwiedervorlage = { readonly faelligAm: string };

/** Was die Übernahme außer dem Abschluss bewirkt hat. */
export type Leadfolgen = {
  readonly werbung: 'uebernommen' | 'widerspruch_bleibt' | 'ohne';
  readonly wiedervorlage: string | null;
};

export type Leadentscheidung = Leadakte & { readonly folgen?: Leadfolgen };

/** WEB-02: der neue Kunde, wie der Vertrieb ihn aus der Anfrage bestätigt. */
export type Leadneukunde =
  | { readonly kundenart: 'privat'; readonly vorname?: string; readonly nachname: string }
  | {
      readonly kundenart: 'geschaeft';
      readonly firmierung: string;
      readonly vorname?: string;
      readonly nachname: string;
    };

export async function zuordnen(
  id: string,
  kunde: string,
  wiedervorlage: Leadwiedervorlage | null = null,
): Promise<Leadentscheidung> {
  return rufe(`/leads/${id}/zuordnen`, {
    verfahren: 'POST',
    rumpf: wiedervorlage === null ? { kunde } : { kunde, wiedervorlage },
  });
}

export async function uebernehmen(
  id: string,
  neu: Leadneukunde,
  wiedervorlage: Leadwiedervorlage | null,
): Promise<Leadentscheidung> {
  return rufe(`/leads/${id}/uebernehmen`, {
    verfahren: 'POST',
    rumpf: wiedervorlage === null ? { neu } : { neu, wiedervorlage },
  });
}

export async function erledigen(id: string, grund: string): Promise<Leadakte> {
  return rufe(`/leads/${id}/erledigen`, {
    verfahren: 'POST',
    rumpf: grund.trim() === '' ? {} : { grund },
  });
}

export async function verwerfen(id: string, grund: string): Promise<Leadakte> {
  return rufe(`/leads/${id}/verwerfen`, { verfahren: 'POST', rumpf: { grund } });
}
